import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Pencil, Trash2, Eye, FileText, PenSquare, Lock } from 'lucide-react';
import { api } from '../hooks/api.js';
import type { PostStatus } from '@blogforge/shared';
import { useCreateGuard, PlanUpgradeNotice } from '../components/PlanGuard.js';

const STATUS_FILTERS = ['all', 'draft', 'review', 'scheduled', 'published', 'archived'] as const;

export function DashboardPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const postGuard = useCreateGuard('post');

  const { data, isLoading } = useQuery({
    queryKey: ['posts', { search, status: statusFilter }],
    queryFn: () =>
      api.posts.list({
        search: search || undefined,
        status: statusFilter === 'all' ? undefined : (statusFilter as PostStatus),
        limit: 30,
      }),
  });

  const createMutation = useMutation({
    mutationFn: () => api.posts.create({ title: 'Untitled post' }),
    onSuccess: (post: unknown) => {
      void qc.invalidateQueries({ queryKey: ['posts'] });
      navigate(`/posts/${(post as { id: string }).id}/edit`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.posts.delete(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['posts'] }),
  });

  const posts = (data as { data: unknown[] } | undefined)?.data ?? [];

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* ── Page header ─────────────────────────────────────── */}
      <header className="relative shrink-0 px-8 pt-8 pb-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="kicker mb-2">
              <span className="h-1.5 w-1.5 rounded-full bg-accent" />
              Content library
            </p>
            <h1 className="text-[22px] font-bold tracking-tight text-ink">Posts</h1>
            <p className="mt-1 text-[13px] text-ink-soft">
              Draft, review and publish clean, portable articles.
            </p>
          </div>
          <button
            onClick={() => !postGuard.allowed || createMutation.mutate()}
            disabled={createMutation.isPending}
            title={postGuard.blockedReason}
            className="btn-primary px-4 py-2 text-[13px]"
          >
            {postGuard.allowed ? (
              <>
                <Plus size={15} strokeWidth={2.4} />
                New post
              </>
            ) : (
              <>
                <Lock size={14} />
                Plan limit reached
              </>
            )}
          </button>
        </div>
      </header>

      {/* ── Free-plan quota notice ────────────────────────────── */}
      {!postGuard.allowed && postGuard.blockedReason && (
        <div className="shrink-0 px-8 pb-4">
          <PlanUpgradeNotice reason={postGuard.blockedReason} />
        </div>
      )}

      {/* ── Toolbar ─────────────────────────────────────────── */}
      <div className="shrink-0 px-8 pb-5">
        <div className="surface-row flex flex-wrap items-center gap-3 px-3.5 py-3">
          <div className="input-with-icon min-w-52 max-w-md flex-1">
            <Search />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search posts…"
              className="field-input bg-bg/60"
            />
          </div>
          <div className="ml-auto flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-line bg-bg/60 p-1">
            {STATUS_FILTERS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-all duration-150 ${
                  statusFilter === s
                    ? 'bg-card text-ink shadow-sm'
                    : 'text-muted hover:text-ink-soft'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Post list ───────────────────────────────────────── */}
      <div className="min-h-0 flex-1 overflow-y-auto px-8 pb-10">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center gap-2 text-sm text-muted">
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-line-strong border-t-accent" />
            Loading posts…
          </div>
        ) : posts.length === 0 ? (
          <div className="flex h-full min-h-72 flex-col items-center justify-center text-center">
            <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-accent/10 text-accent shadow-[inset_0_0_0_1px_var(--color-accent-border)]">
              <FileText size={26} strokeWidth={1.8} />
            </div>
            <h2 className="text-base font-semibold text-ink">No posts here yet</h2>
            <p className="mt-1 max-w-xs text-[13px] text-muted">
              Create your first post and publish clean, portable HTML in minutes.
            </p>
            <button
              onClick={() => createMutation.mutate()}
              className="btn-primary mt-5 px-4 py-2 text-[13px]"
            >
              <Plus size={15} strokeWidth={2.4} />
              Create your first post
            </button>
          </div>
        ) : (
          <div className="space-y-2.5">
            {(posts as Record<string, unknown>[]).map((post) => (
              <div
                key={post['id'] as string}
                className="group surface-row flex items-center gap-4 px-4 py-3.5 transition-all duration-150 hover:border-accent-border hover:shadow-glow"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bg text-muted transition-colors group-hover:text-accent">
                  <PenSquare size={15} />
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-ink">
                    {post['title'] as string}
                  </p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className={`badge badge-${post['status'] as string}`}>
                      {post['status'] as string}
                    </span>
                    <span className="font-mono text-[11.5px] text-muted">
                      /{post['slug'] as string}
                    </span>
                    {Boolean(post['wordCount']) && (
                      <span className="text-xs text-muted">
                        {post['wordCount'] as number} words ·{' '}
                        {post['readingTimeMinutes'] as number} min read
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                  {post['status'] === 'published' && (
                    <a
                      title="Blog Link"
                      href={`/blog/posts/${post['slug'] as string}/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-ghost px-2.5 text-xs"
                    >
                      <Eye size={13} />
                      View
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => navigate(`/posts/${post['id'] as string}/edit`)}
                    className="btn-ghost px-2.5 text-xs"
                  >
                    <Pencil size={13} />
                    Edit
                  </button>
                  <button
                    type="button"
                    title="Archive Post"
                    onClick={() => {
                      if (confirm('Archive this post?')) {
                        deleteMutation.mutate(post['id'] as string);
                      }
                    }}
                    className="btn-ghost px-2.5 text-xs text-danger hover:bg-danger/10 hover:text-danger"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
